import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Grid,
  LinearProgress, Select, MenuItem, FormControl, InputLabel, Paper, Avatar,
} from '@mui/material';
import { TrendingUp, TrendingDown, Radar, Refresh } from '@mui/icons-material';
import dayjs from '../utils/dayjs';
import { trading } from '../services/api';

export default function SignalsPage() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('ALL');

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const queryParams = status === 'ALL' ? { limit: 50 } : { status, limit: 50 };
        const res = await trading.signals(queryParams);
        const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        if (isMounted) setSignals(list);
      } catch (err) {
        console.error('Failed to load signals:', err);
        if (isMounted) setSignals([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    const t = setInterval(load, 10000);
    return () => {
      isMounted = false;
      clearInterval(t);
    };
  }, [status]);

  const safeSignals = Array.isArray(signals) ? signals : [];
  const calls = safeSignals.filter(s => s?.signal_type === 'BUY_CALL');
  const puts = safeSignals.filter(s => s?.signal_type === 'BUY_PUT');

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
            Automated Signal Stream
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Real-time algorithmic trading signals evaluated by indicator & sentiment filters.
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontWeight: 600 }}>Filter Status</InputLabel>
          <Select
            value={status}
            label="Filter Status"
            onChange={e => setStatus(e.target.value)}
            sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
          >
            <MenuItem value="ALL">All Signals</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="EXECUTED">Executed</MenuItem>
            <MenuItem value="IGNORED">Ignored</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Summary KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: '#ffffff', p: 0.5 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.04em' }}>
                  BUY CALL SIGNALS
                </Typography>
                <Avatar sx={{ bgcolor: '#ecfdf5', color: '#10b981', width: 40, height: 40, borderRadius: 2.5 }}>
                  <TrendingUp />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {calls.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: '#ffffff', p: 0.5 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.04em' }}>
                  BUY PUT SIGNALS
                </Typography>
                <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444', width: 40, height: 40, borderRadius: 2.5 }}>
                  <TrendingDown />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {puts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Signals Data Table */}
      <Card sx={{ mb: 4 }}>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
          {loading && safeSignals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>Scanning live market signals...</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TIME</TableCell>
                  <TableCell>SYMBOL</TableCell>
                  <TableCell>SIGNAL</TableCell>
                  <TableCell align="right">STRIKE</TableCell>
                  <TableCell align="right">SPOT</TableCell>
                  <TableCell align="right">OPTION LTP</TableCell>
                  <TableCell align="right">EMA 9</TableCell>
                  <TableCell align="right">EMA 21</TableCell>
                  <TableCell align="right">RSI</TableCell>
                  <TableCell>OI TREND</TableCell>
                  <TableCell>STRENGTH</TableCell>
                  <TableCell align="right">CONFIDENCE</TableCell>
                  <TableCell>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {safeSignals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 6, color: '#64748b' }}>
                      <Radar sx={{ fontSize: 40, color: '#cbd5e1', mb: 1, display: 'block', mx: 'auto' }} />
                      No trading signals found for filter "{status}"
                    </TableCell>
                  </TableRow>
                ) : safeSignals.map(s => (
                  <TableRow key={s.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ color: '#64748b', fontSize: '0.825rem' }}>
                      {s.created_at ? dayjs(s.created_at).format('HH:mm:ss') : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{s.symbol}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.signal_type}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          bgcolor: s.signal_type === 'BUY_CALL' ? '#dcfce7' : '#fee2e2',
                          color: s.signal_type === 'BUY_CALL' ? '#15803d' : '#991b1b',
                        }}
                        icon={s.signal_type === 'BUY_CALL' ? <TrendingUp sx={{ fontSize: '14px !important', color: '#15803d !important' }} /> : <TrendingDown sx={{ fontSize: '14px !important', color: '#991b1b !important' }} />}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{s.strike_price || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₹{parseFloat(s.spot_price || 0).toFixed(0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>₹{parseFloat(s.option_ltp || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: '#64748b' }}>{parseFloat(s.ema9 || 0).toFixed(1)}</TableCell>
                    <TableCell align="right" sx={{ color: '#64748b' }}>{parseFloat(s.ema21 || 0).toFixed(1)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={parseFloat(s.rsi14 || 0).toFixed(1)}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          bgcolor: s.rsi14 > 60 ? '#dcfce7' : s.rsi14 < 40 ? '#fee2e2' : '#f1f5f9',
                          color: s.rsi14 > 60 ? '#15803d' : s.rsi14 < 40 ? '#991b1b' : '#475569',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.oi_trend || 'N/A'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          bgcolor: s.oi_trend === 'BULLISH' ? '#dcfce7' : s.oi_trend === 'BEARISH' ? '#fee2e2' : '#f1f5f9',
                          color: s.oi_trend === 'BULLISH' ? '#15803d' : s.oi_trend === 'BEARISH' ? '#991b1b' : '#475569',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.strength || 'MODERATE'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          bgcolor: s.strength === 'STRONG' ? '#dcfce7' : s.strength === 'MODERATE' ? '#fef3c7' : '#f1f5f9',
                          color: s.strength === 'STRONG' ? '#15803d' : s.strength === 'MODERATE' ? '#b45309' : '#475569',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Math.max(0, parseFloat(s.confidence || 0)))}
                          sx={{ width: 50, height: 6, borderRadius: 3, bgcolor: '#f1f5f9' }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {parseFloat(s.confidence || 0).toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.status || 'PENDING'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          bgcolor: s.status === 'EXECUTED' ? '#dcfce7' : s.status === 'PENDING' ? '#fef3c7' : '#f1f5f9',
                          color: s.status === 'EXECUTED' ? '#15803d' : s.status === 'PENDING' ? '#b45309' : '#475569',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Card>
    </Box>
  );
}
